<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TaccSelect from 'dashboard/components/widgets/conversation/tacc/TaccSelect.vue';
import TaccRemindersAPI from 'dashboard/api/taccReminders';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  loadingAgents: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['saved', 'close']);

const dialogRef = ref(null);
const mode = ref('create');
const currentReminderId = ref(null);
const isSaving = ref(false);
const errorMessage = ref('');

const form = reactive({
  title: '',
  description: '',
  date: '',
  time: '',
  assignee_id: 'me',
});

const isEdit = computed(() => mode.value === 'edit');
const title = computed(() =>
  isEdit.value ? 'Edit reminder' : 'Schedule reminder'
);
const submitLabel = computed(() =>
  isEdit.value ? 'Save reminder' : 'Schedule reminder'
);

const assigneeOptions = computed(() => [
  { value: 'me', label: 'Me' },
  ...props.agents.map(agent => ({
    value: String(agent.id),
    label: agent.available_name || agent.name || agent.email,
  })),
]);

const formatDateInput = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeInput = date => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const resetForm = reminder => {
  const remindAt = reminder?.remind_at
    ? new Date(reminder.remind_at)
    : new Date(Date.now() + 60 * 60 * 1000);

  form.title = reminder?.title || '';
  form.description = reminder?.description || '';
  form.date = formatDateInput(remindAt);
  form.time = formatTimeInput(remindAt);
  form.assignee_id = reminder?.assignee?.id
    ? String(reminder.assignee.id)
    : 'me';
};

const open = (nextMode = 'create', reminder = null) => {
  mode.value = nextMode;
  currentReminderId.value = reminder?.id || null;
  errorMessage.value = '';
  resetForm(reminder);
  dialogRef.value?.open();
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
};

const save = async () => {
  errorMessage.value = '';
  const titleValue = form.title.trim();
  if (!titleValue || !form.date || !form.time) {
    errorMessage.value = 'Add a title, date, and time.';
    return;
  }

  const remindAt = new Date(`${form.date}T${form.time}:00`);
  if (Number.isNaN(remindAt.getTime())) {
    errorMessage.value = 'Choose a valid reminder time.';
    return;
  }

  isSaving.value = true;
  try {
    const payload = {
      title: titleValue,
      description: form.description.trim(),
      remind_at: remindAt.toISOString(),
      assignee_id: form.assignee_id || 'me',
    };

    const response = isEdit.value
      ? await TaccRemindersAPI.updateReminder(
          props.conversationId,
          currentReminderId.value,
          payload
        )
      : await TaccRemindersAPI.createReminder(props.conversationId, payload);

    emit('saved', response.data.reminder);
    close();
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Could not save reminder.';
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    position="top"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="emit('close')"
  >
    <form class="flex flex-col gap-5" @submit.prevent="save">
      <div class="flex items-start gap-3">
        <span
          class="flex size-12 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-brand"
        >
          <span class="i-lucide-bell size-6" />
        </span>
        <div>
          <h3 class="mb-1 text-xl font-semibold leading-7 text-n-slate-12">
            {{ title }}
          </h3>
          <p class="mb-0 text-sm leading-5 text-n-slate-11">
            Get notified to follow up on this conversation at the right time.
          </p>
        </div>
      </div>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-11">Title</span>
        <input
          v-model="form.title"
          class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(31,147,255,0.16)]"
          placeholder="e.g. Follow up on refund status"
          type="text"
        />
      </label>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-11">
          Description (optional)
        </span>
        <textarea
          v-model="form.description"
          class="h-24 resize-none rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(31,147,255,0.16)]"
          placeholder="Add notes for this reminder..."
        />
      </label>

      <div class="grid grid-cols-2 gap-3">
        <label class="flex min-w-0 flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-11">Date</span>
          <input
            v-model="form.date"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(31,147,255,0.16)]"
            type="date"
          />
        </label>
        <label class="flex min-w-0 flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-11">Time</span>
          <input
            v-model="form.time"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(31,147,255,0.16)]"
            type="time"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-11">Remind</span>
        <TaccSelect
          v-model="form.assignee_id"
          :options="assigneeOptions"
          :loading="loadingAgents && assigneeOptions.length <= 1"
          placeholder="Me"
        />
      </label>

      <p v-if="errorMessage" class="mb-0 text-sm text-n-ruby-10">
        {{ errorMessage }}
      </p>

      <div
        class="flex items-center justify-end gap-3 border-t border-n-weak pt-5"
      >
        <Button
          label="Cancel"
          variant="ghost"
          color="blue"
          type="button"
          @click="close"
        />
        <Button
          :label="submitLabel"
          color="blue"
          type="submit"
          :is-loading="isSaving"
        />
      </div>
    </form>
  </Dialog>
</template>
